import styled, { x } from '@xstyled/styled-components';
import React, { useState } from 'react';
import { Flex } from '~/ui/flex/Flex';
import { Item, ItemDefinition, registry, RenderProps } from '../Item';
import { QUIZ_INSTA } from '../itemIds';
import AvatarImage from './avatar.svg';
import { QuizInstaComponent } from './QuizInstaComponent';
import { ItemOfType } from '../../translators/itemHelpers/types';
import { QuizProvider } from '../quiz/QuizStore';
import { FeedbackBlock } from '../quiz/QuizChoice';
import { TrainingState, useTrainingStore } from '~/training/state';
import { select } from '~/lib/select';
import { css } from 'styled-components';

interface Props {
  id: string;
  title: string;
  name: string;
  picture: string;
  questions: ItemOfType<'Question'>['items'];
}

const selectorVisualFeedback = (s: TrainingState) =>
  select(s, 'visualFeedback', 'companyName', 'logoUrl');

const QuizInstaWrapper = styled.div<{ isVideoFinished: boolean }>`
  display: inline-block;
  position: relative;

  .phone-background {
    pointer-events: none;
    z-index: 2;
  }

  .phone {
    .video-js {
      filter: ${p => (p.isVideoFinished ? 'brightness(0.4)' : '')};
    }

    button {
      display: ${p => (p.isVideoFinished ? 'none' : '')};
    }
  }
`;

const StyledQuizInstaHeader = styled.div`
  position: absolute;
  top: 50px;
  left: 7px;
  z-index: 2;
  font-size: 13px;
  color: #ffffff;
  font-weight: 700;
  font-family: system-ui;
  display: flex;
  align-items: center;
  gap: 8px;

  img {
    border-radius: 50%;
  }
  span {
    margin-bottom: 3px;
  }
`;

export const QuizInstaHeader: React.FC<{
  name: string;
  picture: string;
}> = ({ name, picture }) => {
  const { companyName, logoUrl } = useTrainingStore(selectorVisualFeedback);
  return (
    <StyledQuizInstaHeader>
      {picture || logoUrl ? (
        <x.img width={25} height={25} alt="" src={picture || logoUrl} />
      ) : (
        <AvatarImage />
      )}
      <x.span>{name || companyName}</x.span>
    </StyledQuizInstaHeader>
  );
};

const QuizInstaComponentTitle = styled.div`
  padding: 0px 10px;
  position: absolute;
  color: #ffffff;
  top: 231px;
  width: 100%;
  z-index: 2;
  text-align: center;
`;

const FeedbackBlockWrapper = styled.div`
  @keyframes slideInFromBottom {
    0% {
      transform: translateY(100%);
      opacity: 0;
    }
    100% {
      transform: translateY(0);
      opacity: 1;
    }
  }

  width: 100%;
  position: absolute;
  z-index: 4;
  bottom: 155px;
  ${() => {
    const { visualFeedback } = useTrainingStore(selectorVisualFeedback);
    const sec = visualFeedback ? '0.7s' : '0s';

    return css`
      animation: ${sec} ease-out 0s 1 slideInFromBottom;
    `;
  }}
`;

export type QuizInstaDefinition = ItemDefinition<typeof QUIZ_INSTA, Props>;

export class QuizInsta extends Item<typeof QUIZ_INSTA, Props> {
  protected render({
    id,
    title,
    name,
    picture,
    children,
    questions
  }: RenderProps<Props>) {
    const [isVideoFinished, setVideoFinished] = useState(false);
    const [isQuestionAnswered, setQuestionAnswered] = useState(false);

    return (
      <QuizProvider quizId={id || 'default'}>
        <Flex variant="center" position="relative" my="md">
          <QuizInstaWrapper isVideoFinished={isVideoFinished}>
            <QuizInstaHeader name={name} picture={picture} />
            {isQuestionAnswered && (
              <FeedbackBlockWrapper aria-live="polite">
                <FeedbackBlock w={'100%'} />
              </FeedbackBlockWrapper>
            )}
            {isVideoFinished && (
              <QuizInstaComponentTitle aria-live="polite">
                {title}
              </QuizInstaComponentTitle>
            )}
            {children}
            <QuizInstaComponent
              id={id}
              questions={questions}
              isVideoFinished={isVideoFinished}
              setVideoFinished={setVideoFinished}
              setQuestionAnswered={setQuestionAnswered}
            />
          </QuizInstaWrapper>
        </Flex>
      </QuizProvider>
    );
  }
}

registry[QUIZ_INSTA] = QuizInsta;
